import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import * as THREE from 'three';
import { useEditorStore } from './index';
import useFinalizationDataStore from './useFinalizationDataStore';
import {
  getSurfaceNormalAtPoint,
  projectSeedOntoSurfaceAtTarget,
} from '../tools/trimline/trimlineHelpers';

const BEZIER_HANDLE_DISTANCE = 3;

const useTrimlineStore = create(
  devtools((set, get) => ({
    // Trimline Tool
    trimline: {
      enabled: true,
      points: [
        // Default 4 points: front, right, back, left (curve order)
        { x: 0, y: 0, z: 5 },    // Front
        { x: 4, y: 4, z: 0 },    // Right
        { x: 0, y: 0, z: -5 },   // Back
        { x: -4, y: 4, z: 0 }    // Left
      ],
      selectedPointIndex: null,
      showBezierHandles: false,
      bezierHandles: [
        { in: { x: -BEZIER_HANDLE_DISTANCE, y: 0, z: 0 }, out: { x: BEZIER_HANDLE_DISTANCE, y: 0, z: 0 } },
        { in: { x: -BEZIER_HANDLE_DISTANCE, y: 0, z: BEZIER_HANDLE_DISTANCE }, out: { x: BEZIER_HANDLE_DISTANCE, y: 0, z: -BEZIER_HANDLE_DISTANCE } },
        { in: { x: BEZIER_HANDLE_DISTANCE, y: 0, z: 0 }, out: { x: -BEZIER_HANDLE_DISTANCE, y: 0, z: 0 } },
        { in: { x: BEZIER_HANDLE_DISTANCE, y: 0, z: -BEZIER_HANDLE_DISTANCE }, out: { x: -BEZIER_HANDLE_DISTANCE, y: 0, z: BEZIER_HANDLE_DISTANCE } }
      ],
      previewVisible: true,
    },

    // Patela tendon reference point (used to seed the trimline saddle shape)
    patelaPoint: null, // {x,y,z} in world coords (aligned mesh space)
    isSelectingPatela: false,

    applied: false,
    originalMesh: null,
    previewMesh: null,

    // Draft restoration flag
    isAppliedFromDraft: false,
    
    // Store upper mesh from trimline cutting for thickness top mesh generation
    upperMesh: null,
    
    // Store initial points generated from mesh surface projection
    initialPoints: null,
    initialBezierHandles: null,

    // Trigger for re-initialization
    shouldReinitialize: false,

    // Actions
    setTrimline: (data) => set({ trimline: { ...get().trimline, ...data } }),

    requestReinitialize: () => set({ shouldReinitialize: true }),

    clearReinitializeFlag: () => set({ shouldReinitialize: false }),

    // ---- Patela selection helpers ----
    startPatelaSelection: () => set({ isSelectingPatela: true }),
    stopPatelaSelection: () => set({ isSelectingPatela: false }),
    setPatelaPoint: (point) => {
      if (!point) return;
      set({ patelaPoint: point, isSelectingPatela: false });
    },
    clearPatelaPoint: () => set({ patelaPoint: null, isSelectingPatela: false }),
    
    updatePoint: (index, position) => {
      const { trimline } = get();
      const newPoints = [...trimline.points];
      newPoints[index] = position;
      set({ trimline: { ...trimline, points: newPoints } });
    },

    selectPoint: (index) => {
      const { trimline } = get();
      set({ 
        trimline: { 
          ...trimline, 
          selectedPointIndex: index,
          showBezierHandles: index !== null
        }
      });
    },

    updateBezierHandle: (pointIndex, handleType, position) => {
      const { trimline } = get();
      const newHandles = [...trimline.bezierHandles];
      newHandles[pointIndex] = {
        ...newHandles[pointIndex],
        [handleType]: position
      };
      set({ trimline: { ...trimline, bezierHandles: newHandles } });
    },

    mirrorLeftToRight: () => {
      const { trimline } = get();
      const { sceneRefs } = useEditorStore.getState();
      const shadedMesh = sceneRefs.shadedMesh;
      const bvh = shadedMesh?.geometry?.boundsTree;

      const newPoints = [...trimline.points];
      const newHandles = [...trimline.bezierHandles];

      // Mirror left (index 3) to right (index 1)
      if (newPoints.length === 4 && shadedMesh && bvh) {
        if (!shadedMesh.geometry.boundingBox) shadedMesh.geometry.computeBoundingBox();
        const bbox = shadedMesh.geometry.boundingBox.clone();
        bbox.applyMatrix4(shadedMesh.matrixWorld);
        const meshCenter = bbox.getCenter(new THREE.Vector3());

        const projectMirroredPoint = (sourcePoint) =>
          projectSeedOntoSurfaceAtTarget(
            -sourcePoint.x,
            sourcePoint.z,
            sourcePoint.y,
            shadedMesh,
            bvh,
            meshCenter
          );

        newPoints[1] = projectMirroredPoint(newPoints[3]);

        // Helper to mirror handles with surface normal rotation
        const mirrorHandlesWithNormalRotation = (sourcePointIndex, targetPointIndex, sourcePoint, targetPoint, sourceHandles) => {
          try {
            // Get surface normals at source and target points
            const sourceNormal = getSurfaceNormalAtPoint(sourcePoint, shadedMesh, bvh);
            const targetNormal = getSurfaceNormalAtPoint(targetPoint, shadedMesh, bvh);

            if (!sourceNormal || !targetNormal) {
              // Fallback to simple X mirror
              return {
                in: { x: -sourceHandles.in.x, y: sourceHandles.in.y, z: sourceHandles.in.z },
                out: { x: -sourceHandles.out.x, y: sourceHandles.out.y, z: sourceHandles.out.z }
              };
            }

            // Create handle vectors
            const inVec = new THREE.Vector3(sourceHandles.in.x, sourceHandles.in.y, sourceHandles.in.z);
            const outVec = new THREE.Vector3(sourceHandles.out.x, sourceHandles.out.y, sourceHandles.out.z);

            // Preserve original lengths
            const inLength = inVec.length();
            const outLength = outVec.length();

            // First, mirror the handles in X
            const inMirrored = new THREE.Vector3(-inVec.x, inVec.y, inVec.z);
            const outMirrored = new THREE.Vector3(-outVec.x, outVec.y, outVec.z);

            // Calculate rotation from source normal to target normal
            const rotation = new THREE.Quaternion();
            if (sourceNormal.dot(targetNormal) < 0.9999) { // Not parallel
              rotation.setFromUnitVectors(
                sourceNormal.clone().normalize(),
                targetNormal.clone().normalize()
              );

              // Apply rotation to mirrored handles
              inMirrored.applyQuaternion(rotation);
              outMirrored.applyQuaternion(rotation);
            }

            // Project into target tangent plane (perpendicular to target normal)
            const projectToTangentPlane = (vec, normal, targetLen) => {
              const projected = vec.clone().sub(normal.clone().multiplyScalar(vec.dot(normal)));
              const len = projected.length();
              if (len < 1e-6) {
                // If projection fails, use a perpendicular vector
                const fallback = new THREE.Vector3(1, 0, 0);
                const perp = fallback.clone().sub(normal.clone().multiplyScalar(fallback.dot(normal))).normalize();
                return perp.multiplyScalar(targetLen);
              }
              return projected.multiplyScalar(targetLen / len);
            };

            const inFinal = projectToTangentPlane(inMirrored, targetNormal, inLength);
            const outFinal = projectToTangentPlane(outMirrored, targetNormal, outLength);

            return {
              in: { x: inFinal.x, y: inFinal.y, z: inFinal.z },
              out: { x: outFinal.x, y: outFinal.y, z: outFinal.z }
            };
          } catch (error) {
            // Fallback to simple X mirror on error
            return {
              in: { x: -sourceHandles.in.x, y: sourceHandles.in.y, z: sourceHandles.in.z },
              out: { x: -sourceHandles.out.x, y: sourceHandles.out.y, z: sourceHandles.out.z }
            };
          }
        };

        newHandles[1] = mirrorHandlesWithNormalRotation(3, 1, newPoints[3], newPoints[1], newHandles[3]);
      } else if (newPoints.length === 4) {
        // Fallback for 4-point: simple mirror without mesh projection (left→right)
        newPoints[1] = { x: -newPoints[3].x, y: newPoints[3].y, z: newPoints[3].z };
        newHandles[1] = {
          in: { x: -newHandles[3].in.x, y: newHandles[3].in.y, z: newHandles[3].in.z },
          out: { x: -newHandles[3].out.x, y: newHandles[3].out.y, z: newHandles[3].out.z }
        };
      }

      set({ trimline: { ...trimline, points: newPoints, bezierHandles: newHandles } });
    },

    setPreviewVisible: (visible) => {
      const { trimline } = get();
      set({ trimline: { ...trimline, previewVisible: visible } });
    },

    setApplied: (status) => set({ applied: status }),
    setPreviewMesh: (mesh) => {
      set({ previewMesh: mesh });
    },
    setOriginalMesh: (mesh) => {
      set({ originalMesh: mesh });
    },

    setUpperMesh: (mesh) => {
      set({ upperMesh: mesh });
    },

    setInitialPoints: (points, bezierHandles) => {
      set({ initialPoints: points, initialBezierHandles: bezierHandles });
    },

    // Command/Undo support (apply/undo trimline)
    isTrimlineApplied: false,
    setIsTrimlineApplied: (status) => set({ isTrimlineApplied: status }),

    currentTrimlineCommand: null,
    setCurrentTrimlineCommand: (command) => set({ currentTrimlineCommand: command }),

    createTrimlineCommand: () => {
      const { trimline, previewMesh, originalMesh } = get();
      const { sceneRefs, replaceShadedMesh } = useEditorStore.getState();
      const originalMaterial = sceneRefs.material;

      if (!previewMesh || !trimline?.enabled) return null;

      const currentMesh = sceneRefs.shadedMesh;
      const currentGeometry = currentMesh.geometry;
      const currentMatrix = currentMesh.matrix.clone();
      const originalTrimline = JSON.parse(JSON.stringify(trimline));

      // Clone geometry from preview (finalized result)
      const cloneGeom = new THREE.BufferGeometry().copy(previewMesh.geometry);
      const cloneMatrix = new THREE.Matrix4().copy(previewMesh.matrix);

      return {
        execute: () => {
          // Remove preview visual
          const latest = get().previewMesh;
          if (latest && latest.isMesh) {
            latest.parent?.remove(latest);
            latest.geometry?.dispose?.();
            latest.material?.dispose?.();
          }

          // **CRITICAL FIX**: Ensure geometry has proper spatial structures before applying
          cloneGeom.computeVertexNormals();
          cloneGeom.computeBoundingBox();
          cloneGeom.computeBoundingSphere();
          // Dispose any stale BVH tree (will be rebuilt by tools that need it)
          if (cloneGeom.boundsTree) {
            cloneGeom.disposeBoundsTree();
          }

          // Use the original universal material directly to maintain ghost/color mode toggles
          // Enable vertex colors on the material if geometry has color attribute
          if (cloneGeom.attributes.color && !originalMaterial.vertexColors) {
            originalMaterial.vertexColors = true;
            originalMaterial.needsUpdate = true;
          }

          const newMesh = new THREE.Mesh(cloneGeom, originalMaterial);
          newMesh.matrix.copy(cloneMatrix);
          // Decompose matrix into TRS and enable auto-updates for proper transform handling
          newMesh.matrix.decompose(newMesh.position, newMesh.quaternion, newMesh.scale);
          newMesh.matrixAutoUpdate = true;
          replaceShadedMesh(newMesh);

          // Properly dispose preview mesh including BVH tree
          if(previewMesh) {
            previewMesh.parent?.remove(previewMesh);
            // Dispose BVH tree if present
            if (previewMesh.geometry?.boundsTree) {
              previewMesh.geometry.disposeBoundsTree();
            }
            previewMesh.geometry?.dispose?.();
            previewMesh.material?.dispose?.();
          }
          
          set({
            isTrimlineApplied: true,
            previewMesh: null,
            applied: true,
            isAppliedFromDraft: false, // Clear draft flag on manual apply
            trimline: { ...get().trimline, enabled: false, previewVisible: false }
          });

          // Save to finalization store for draft and review purposes
          useFinalizationDataStore.getState().saveTrimline();
        },
        undo: () => {
          const restoredMesh = originalMesh || new THREE.Mesh(currentGeometry, originalMaterial);
          restoredMesh.matrix.copy(currentMatrix);
          // Decompose matrix into TRS and enable auto-updates for proper transform handling
          restoredMesh.matrix.decompose(restoredMesh.position, restoredMesh.quaternion, restoredMesh.scale);
          restoredMesh.matrixAutoUpdate = true;
          replaceShadedMesh(restoredMesh);
          set({ 
            isTrimlineApplied: false, 
            trimline: originalTrimline, 
            applied: false,
          });
        }
      };
    },

    // Reset all data
    reset: () => {
      const { initialPoints, initialBezierHandles } = get();
      
      // Use stored initial points if available, otherwise use 4-point defaults
      const resetPoints = initialPoints || [
        { x: 0, y: 0, z: 5 },   // Front
        { x: 4, y: 4, z: 0 },   // Right
        { x: 0, y: 0, z: -5 },  // Back
        { x: -4, y: 4, z: 0 }   // Left
      ];
      
      const resetBezierHandles = initialBezierHandles || [
        { in: { x: -1, y: 0, z: 0 }, out: { x: 1, y: 0, z: 0 } },
        { in: { x: -1, y: 0, z: 1 }, out: { x: 1, y: 0, z: -1 } },
        { in: { x: 1, y: 0, z: 0 }, out: { x: -1, y: 0, z: 0 } },
        { in: { x: 1, y: 0, z: -1 }, out: { x: -1, y: 0, z: 1 } }
      ];
      
      set({
        trimline: {
          enabled: true,
          points: [...resetPoints],
          selectedPointIndex: null,
          showBezierHandles: false,
          bezierHandles: [...resetBezierHandles],
          previewVisible: true,
          previewMesh: null
        },
        previewMesh: null,
        originalMesh: null,
        upperMesh: null,

        // Reset patela selection so the user can re-seed the saddle.
        patelaPoint: null,
        isSelectingPatela: true
      });
    }
  }))
);

export default useTrimlineStore;